import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { api } from '@/lib/api';
import type { ProviderBalanceEntryType, ProviderBalanceUnit } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const fmtUsd = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `$${v.toFixed(2)}`;

/**
 * Per-provider detail: balance-vs-spend reconciliation, a manual balance-entry
 * form (observation / top-up / refund / adjustment), the observation history
 * chart, and the recent snapshot log. Caps are edited separately on the
 * overview page.
 */
export function ProviderDetailPanel({
  provider,
  consoleHint,
  onClose,
}: {
  provider: string;
  consoleHint?: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.adminProviders.detail(provider),
    queryFn: () => api.adminGetProviderDetail(provider),
  });

  const [entryType, setEntryType] = useState<ProviderBalanceEntryType>('observation');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<ProviderBalanceUnit>('usd');
  const [usdEstimate, setUsdEstimate] = useState('');
  const [note, setNote] = useState('');

  const recordMutation = useMutation({
    mutationFn: () =>
      api.adminCreateProviderSnapshot(provider, {
        entryType,
        balanceAmount: Number(amount) || 0,
        balanceUnit: unit,
        balanceUsdEstimate: unit === 'usd' ? undefined : usdEstimate ? Number(usdEstimate) : null,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      setAmount('');
      setUsdEstimate('');
      setNote('');
      qc.invalidateQueries({ queryKey: queryKeys.adminProviders.detail(provider) });
      qc.invalidateQueries({ queryKey: queryKeys.adminProviders.overview() });
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => api.adminRefreshProviderBalance(provider),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adminProviders.detail(provider) });
      qc.invalidateQueries({ queryKey: queryKeys.adminProviders.overview() });
    },
  });
  const refreshResult = refreshMutation.data;

  const recon = data?.reconciliation;
  const chartData = (data?.snapshots ?? [])
    .filter((s) => s.entryType === 'observation')
    .map((s) => ({
      date: s.observedAt.slice(0, 10),
      balance: s.balanceUsdEstimate ?? (s.balanceUnit === 'usd' ? s.balanceAmount : null),
      expected: s.expectedUsd,
    }))
    .reverse();

  const field =
    'w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{provider} — balance &amp; reconciliation</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        {isError && (
          <p className="text-destructive mb-3">
            {error instanceof Error ? error.message : 'Failed to load'}
          </p>
        )}
        {isLoading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : (
          <>
            {/* Reconciliation */}
            <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-4">
              <Stat label="Last observed" value={fmtUsd(recon?.observedBalanceUsd)} />
              <Stat label="Spend since" value={fmtUsd(recon?.recordedSpendSinceUsd)} />
              <Stat label="Expected now" value={fmtUsd(recon?.expectedBalanceUsd)} />
              <Stat
                label="Unexplained drift"
                value={fmtUsd(recon?.driftUsd)}
                tone={recon?.flagged ? 'text-red-600 dark:text-red-400 font-semibold' : undefined}
              />
            </div>
            {recon?.flagged && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                ⚠ Drift exceeds tolerance — recorded spend doesn&apos;t match the observed balance.
                Check for untracked usage or an unrecorded top-up/refund.
              </p>
            )}

            {/* Live balance refresh (providers with a balance/cost API). */}
            <div className="mb-5 flex items-center gap-3">
              <button
                type="button"
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending}
                className="px-3 py-1.5 text-sm border border-border rounded-lg disabled:opacity-50"
              >
                {refreshMutation.isPending ? 'Refreshing…' : 'Refresh live balance'}
              </button>
              {refreshResult && !refreshResult.supported && (
                <span className="text-xs text-muted-foreground">
                  No live balance API for this provider — use manual entry.
                </span>
              )}
              {refreshResult?.result && (
                <span className="text-xs text-muted-foreground">
                  {refreshResult.result.ok
                    ? `${refreshResult.result.kind === 'cost' ? 'Spend MTD' : 'Balance'}: ${refreshResult.result.amount ?? '—'} ${refreshResult.result.unit}`
                    : `Unavailable: ${refreshResult.result.error ?? 'not configured'}`}
                </span>
              )}
            </div>
            {consoleHint && (
              <p className="text-xs text-muted-foreground mb-4">
                Where to find it:{' '}
                <a
                  href={consoleHint}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  {consoleHint}
                </a>
              </p>
            )}

            {/* Chart */}
            {chartData.length > 1 && (
              <div className="h-48 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="balance" name="Observed" stroke="#2563eb" dot />
                    <Line
                      type="monotone"
                      dataKey="expected"
                      name="Expected"
                      stroke="#9ca3af"
                      strokeDasharray="4 4"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Manual entry */}
            <div className="border border-border rounded-lg p-4 mb-5">
              <h3 className="text-sm font-semibold mb-3">Record balance / movement</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Type</label>
                  <select
                    aria-label="Entry type"
                    value={entryType}
                    onChange={(e) => setEntryType(e.target.value as ProviderBalanceEntryType)}
                    className={field}
                  >
                    <option value="observation">Observation (current balance)</option>
                    <option value="top_up">Top-up (+)</option>
                    <option value="refund">Refund (+)</option>
                    <option value="adjustment">Adjustment (± signed)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Unit</label>
                  <select
                    aria-label="Unit"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value as ProviderBalanceUnit)}
                    className={field}
                  >
                    <option value="usd">USD</option>
                    <option value="credits">credits</option>
                    <option value="characters">characters</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Amount</label>
                  <input
                    type="number"
                    aria-label="Amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className={field}
                    placeholder="e.g. 99.75"
                  />
                </div>
                {unit !== 'usd' && (
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">USD estimate</label>
                    <input
                      type="number"
                      aria-label="USD estimate"
                      value={usdEstimate}
                      onChange={(e) => setUsdEstimate(e.target.value)}
                      className={field}
                    />
                  </div>
                )}
                <div className="col-span-2">
                  <label className="block text-xs text-muted-foreground mb-1">Note</label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className={field}
                  />
                </div>
              </div>
              {recordMutation.isError && (
                <p className="text-destructive text-sm mt-2">
                  {recordMutation.error instanceof Error ? recordMutation.error.message : 'Failed'}
                </p>
              )}
              <button
                type="button"
                onClick={() => recordMutation.mutate()}
                disabled={recordMutation.isPending || !amount}
                className="mt-3 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg disabled:opacity-50"
              >
                {recordMutation.isPending ? 'Saving…' : 'Record'}
              </button>
            </div>

            {/* History */}
            <h3 className="text-sm font-semibold mb-2">Recent entries</h3>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="p-2">Date</th>
                    <th className="p-2">Type</th>
                    <th className="p-2">Amount</th>
                    <th className="p-2">Drift</th>
                    <th className="p-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.snapshots ?? []).slice(0, 20).map((s) => (
                    <tr key={s.snapshotId} className="border-b border-border last:border-0">
                      <td className="p-2 text-muted-foreground">{s.observedAt.slice(0, 10)}</td>
                      <td className="p-2">{s.entryType}</td>
                      <td className="p-2">
                        {s.balanceAmount} {s.balanceUnit}
                      </td>
                      <td className="p-2">{fmtUsd(s.driftUsd)}</td>
                      <td className="p-2 text-muted-foreground truncate max-w-[160px]">
                        {s.note ?? ''}
                      </td>
                    </tr>
                  ))}
                  {(data?.snapshots ?? []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-3 text-center text-muted-foreground">
                        No entries yet — record the current balance to start reconciling.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-base font-semibold ${tone ?? ''}`}>{value}</div>
    </div>
  );
}
